import React, { useCallback } from 'react';
import { graphql } from 'react-relay';

import Avatar from '@atlaskit/avatar';
import AvatarGroup, { type AvatarGroupProps } from '@atlaskit/avatar-group';

import { ConfluencePageAri } from '@atlassian/ari';

import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import type {
	useRealtimeAccessRequestsFlagsQuery$data,
	useRealtimeAccessRequestsFlagsQuery$variables,
} from './__generated__/useRealtimeAccessRequestsFlagsQuery.graphql';

const REALTIME_ACCESS_REQUEST_FLAG_ID = 'realtime-access-request-flag-id';
const EXPIRE_IN_FIVE_MINUTES = 300; // in seconds

interface RealtimeAccessRequestsFlagsProps {
	contentType: string;
	contentId: string;
	flags: FlagsStateContainer;
}

const UseRealtimeAccessRequestsQuery = graphql`
	query useRealtimeAccessRequestsFlagsQuery(
		$cloudId: ID!
		$contentId: ID!
		$duration: Int = 525960
		$limit: Int = 10
		$start: String
	) {
		confluence_getLatestPendingRequests(
			cloudId: $cloudId
			contentId: $contentId
			duration: $duration
			limit: $limit
			start: $start
		) {
			latestPendingRequest {
				id
				requestAccessType
				creatorAaid
				creationDateTimestamp
				user {
					id
					displayName
					photos {
						isPrimary
						value
					}
				}
			}
		}
	}
`;

export const UseRealtimeAccessRequestsMutation = graphql`
	mutation useRealtimeAccessRequestsFlagsMutation(
		$cloudId: ID!
		$updateContentAccessRequestInput: ConfluenceUpdateContentAccessRequestInput!
	) {
		confluence_updateContentAccessRequest(
			cloudId: $cloudId
			updateContentAccessRequestInput: $updateContentAccessRequestInput
		) {
			success
			errors {
				extensions {
					errorType
					statusCode
					... on ConfluenceUpdateContentAccessRequestMutationErrorExtension {
						displayName
						processedRequestStatus
					}
				}
				message
			}
		}
	}
`;

const getLastSeenRealtimeRequestCreatedAt = () => {
	const lastSeenRequestCreatedAt = Number(
		localStorage.getItem(keys.LAST_SHOWN_REALTIME_REQUEST_CREATED_AT),
	);

	return Number.isNaN(lastSeenRequestCreatedAt) ? lastSeenRequestCreatedAt : 0;
};

// Called in response to a ping that there is a real-time access request
export const useRealtimeAccessRequestsFlags = ({
	contentId,
	contentType,
	flags,
}: RealtimeAccessRequestsFlagsProps) => {
	const { cloudId } = useSessionData();

	const maybeShowRealtimeFlag = useCallback(
		async (data: useRealtimeAccessRequestsFlagsQuery$data) => {
			/**
			 *   1. ✅ Filter out any requests that appeared before the last cached timestamp (i.e. ones user should have seen
			 *
			 * 	 3. ✅ Cache the current latest request creation time as last seen
			 */
			const accessRequests = data.confluence_getLatestPendingRequests?.latestPendingRequest ?? [];
			const filteredRequests = accessRequests.filter((request) => {
				return true;
				// Only creation dates after the last seen one or last 5 minutes
				// @ts-ignore
				return request?.creationDateTimeStamp < getLastSeenRealtimeRequestCreatedAt();
			});

			// Cache latest request if it exists
			// @ts-ignore
			const latestRequestCreatedAt = filteredRequests[0]?.creationDateTimeStamp;
			if (latestRequestCreatedAt) {
				localStorage.setItem(
					keys.LAST_SHOWN_REALTIME_REQUEST_CREATED_AT,
					latestRequestCreatedAt,
					EXPIRE_IN_FIVE_MINUTES,
				);
			}

			/**
			 * 			✅ If 0 requests
			 * 				bail
			 * 			✅ Hide any current flags
			 * 	    	IF only 1 request
			 *       		Also validate if blocked acccess needs to show
			 *           		✅ If no blockage
			 *               		✅ show regular flag with Approve / Deny buttons
			 * 						16s auto-dismiss
			 *           		If blockage
			 *               		If empowered
			 *                   		show blocked flag with extra copy and Approve / Deny buttons
			 * 							16s auto-dismiss
			 *               		If not empowered
			 *                   		Don't show flag
			 *   		IF more than 1 request
			 *       		show regular flag for many users only with "View detils" CTA
			 * 					✅ Ensure 2 and more than 2 requests are handled
			 * 					✅ latest request on top
			 * 					16s auto-dismiss
			 */

			if (filteredRequests.length === 0) {
				return;
			}

			await flags.hideFlag(REALTIME_ACCESS_REQUEST_FLAG_ID);

			/**
			 * Only 1 access request.
			 * Can directly Approve / Deny.
			 * Also checks blocked access.
			 * */
			if (filteredRequests.length === 1 && filteredRequests[0]) {
				// check blocked access
				// show approve / deny flag with / eventually with blocked access
				const onlyRequest = filteredRequests[0];
				const name = onlyRequest?.user?.displayName;
				const photo =
					onlyRequest?.user?.photos?.find((photo) => photo?.isPrimary)?.value ?? undefined;
				const accessType = onlyRequest?.requestAccessType;

				if (!name) {
					return;
				}

				await flags.showCustomFlag({
					id: REALTIME_ACCESS_REQUEST_FLAG_ID,
					customIcon: <Avatar name={name} src={photo} />,
					// Will need 2 translation entires for this one. One View and one Edit
					title: `${name} wants to ${accessType.toLowerCase()} this content`,
					isAutoDismiss: true,
					autoDismissSeconds: 16,
					actions: [
						{
							content: 'Approve',
						},
						{
							content: 'Deny',
						},
					],
				});
			} else {
				/**
				 * Flag for many access requests.
				 * Only CTA is to open the Share dialog -> Access Requests.
				 * */
				const avatarData: AvatarGroupProps['data'] = filteredRequests
					.filter((request) => request?.user?.displayName && request?.requestAccessType)
					.map((request) => ({
						name: request?.user?.displayName!,
						src: request?.user?.photos?.find((photo) => photo?.isPrimary)?.value ?? undefined,
						key: request?.user?.id!,
					}));

				const flagTitle =
					filteredRequests.length === 2
						? `${avatarData[0].name} and ${avatarData[1].name} want to access your content`
						: `${avatarData[0].name} and ${avatarData.length - 1} others want to access your content`;

				await flags.showCustomFlag({
					id: REALTIME_ACCESS_REQUEST_FLAG_ID,
					customIcon: <AvatarGroup data={avatarData} maxCount={2} />,
					title: flagTitle,
					isAutoDismiss: true,
					autoDismissSeconds: 16,
					actions: [
						{
							content: 'Review requests',
						},
					],
				});
			}
		},
		[flags],
	);

	const { refetch } = useQueryInterop<
		useRealtimeAccessRequestsFlagsQuery$data,
		useRealtimeAccessRequestsFlagsQuery$variables
	>(UseRealtimeAccessRequestsQuery, {
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'network-only',
		variables: {
			cloudId,
			contentId,
			duration: 600000,
		},
		onCompleted: maybeShowRealtimeFlag,
	});

	const fetchLatestPendingRequests = useCallback(async () => {
		try {
			await refetch();
		} catch (e) {
			// Maybe log something
			// We don't show error messages for realtime requests to reduce noise
			markErrorAsHandled(e);
		}
	}, [refetch]);

	useSubscriptionEvent({
		contentId,
		contentType,
		delta: 'contentAccessRequested',
		subscriberId: ConfluencePageAri.create({
			siteId: cloudId,
			pageId: contentId,
		}).toString(),
		onDataReceived: fetchLatestPendingRequests,
		/** TODO: Also monitor for tab inactivity and disable the subscription */
		skip: false,
	});
};
